import React, { useState, useEffect, useRef } from 'react';
import { db } from '../firebaseConfig';
import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from 'firebase/firestore';
import { Check, Plus, Trash2, ChevronLeft, ChevronRight, Loader, Calendar, CalendarPlus, CalendarCheck, MessageSquare, GripVertical } from 'lucide-react';
import { format, addDays, subDays } from 'date-fns';
import GoogleCalendarPanel from './GoogleCalendarPanel';

// 1. Static data outside the component
const quotes = [
  { text: "Imagination is more important than knowledge. Knowledge is limited; imagination encircles the world.", author: "Albert Einstein" },
  { text: "The main thing is to keep the main thing the main thing.", author: "Stephen Covey" },
  { text: "Be the change you wish to see in the world.", author: "Mahatma Gandhi" },
  { text: "Your attitude, not your aptitude, will determine your altitude.", author: "Zig Ziglar" },
  { text: "I have a dream that one day this nation will rise up and live out the true meaning of its creed.", author: "Martin Luther King Jr" },
  { text: "Don't wait for permission to be remarkable.", author: "Seth Godin" },
  { text: "Life is like riding a bicycle. To keep your balance, you must keep moving.", author: "Albert Einstein" },
  { text: "Everything you want is on the other side of fear.", author: "Jack Canfield" },
  { text: "The future depends on what you do today.", author: "Mahatma Gandhi" },
  { text: "The time is always right to do what is right.", author: "Martin Luther King Jr" },
  { text: "I will waste not even a precious second today in anger or hate or jealousy or selfishness. I know that the seeds I sow I will harvest, because every action, good or bad, is always followed by an equal reaction. I will plant only good seeds this day.", author: "Og Mandino" },
  { text: "You have more fun and enjoy more financial success when you stop trying to get what you want and start helping other people get what they want.", author: "Spencer Johnson" },
  { text: "Don't wish it was easier; wish you were better. Don't wish for fewer problems; wish for more skills.", author: "Jim Rohn" },
  { text: "Begin with the end in mind.", author: "Stephen Covey" },
  { text: "You have brains in your head. You have feet in your shoes. You can steer yourself any direction you choose.", author: "Dr. Seuss" },
  { text: "If you can't explain it simply, you don't understand it well enough.", author: "Albert Einstein" },
  { text: "I am here for a purpose and that purpose is to grow into a mountain, not to shrink to a grain of sand.", author: "Og Mandino" },
  { text: "When you're dealing with a person who is coming from a paradigm of Win/Lose, the relationship is still the key. You make deposits into the Emotional Bank Account through genuine courtesy, respect and appreciation for that person and for the other point of view.  You stay longer in the communication process.  You listen more. You express yourself with greater courage. That very process is a tremendous deposit in the Emotional Bank Account.", author: "Stephen Covey" },
  { text: "Remember that a person's name is to that person the sweetest and most important sound in any language.", author: "Dale Carnegie" },
  { text: "Darkness cannot drive out darkness; only light can do that. Hate cannot drive out hate; only love can do that.", author: "Martin Luther King Jr" },
  { text: "You were born to win, but to be a winner, you must plan to win, prepare to win, and expect to win.", author: "Zig Ziglar" },
  { text: "Not all of us can do great things. But we can do small things with great love.", author: "Mother Teresa" },
  { text: "The key is not to prioritize what's on your schedule, but to schedule your priorities.", author: "Stephen Covey" },
  { text: "The secret of getting ahead is getting started.", author: "Mark Twain" },
  { text: "Change will not come if we wait for some other person or some other time. We are the ones we've been waiting for.", author: "Barack Obama" },
  { text: "Why fit in when you were born to stand out?", author: "Dr. Seuss" },
  { text: "Success is not to be pursued; it is to be attracted by the person you become.", author: "Jim Rohn" },
  { text: "Be kind whenever possible. It is always possible.", author: "Dalai Lama" },
  { text: "In a gentle way, you can shake the world.", author: "Mahatma Gandhi" },
  { text: "The important thing is not to stop questioning. Curiosity has its own reason for existing.", author: "Albert Einstein" },
  { text: "Seek first to understand, then to be understood.", author: "Stephen Covey" },
  { text: "Success is the progressive realization of worthwhile, predetermined personal goals.", author: "Jack Canfield" },
  { text: "The person who goes farthest is generally the one who is willing to do and dare.", author: "Dale Carnegie" },
  { text: "You must take personal responsibility. You cannot change the circumstances, the seasons, or the wind, but you can change yourself.", author: "Jim Rohn" },
  { text: "Not everything that can be counted counts and not everything that counts can be counted.", author: "William Bruce Cameron" },
  { text: "I have decided to stick with love. Hate is too great a burden to bear.", author: "Martin Luther King Jr" },
  { text: "If you want others to be happy, practice compassion. If you want to be happy, practice compassion.", author: "Dalai Lama" },
  { text: "The only way to do great work is to love what you do.", author: "Albert Einstein" },
  { text: "The goal isn't to be perfect by the end of the day. The goal is to be better than you were yesterday.", author: "Seth Godin" },
  { text: "The two most important days in your life are the day you are born and the day you find out why.", author: "Mark Twain" },
  { text: "Integrity is the foundation upon which all other qualities build.", author: "Stephen Covey" },
  { text: "Synergy is everywhere in nature. Nothing is more important than the people you surround yourself with.", author: "Stephen Covey" },
  { text: "If you judge people, you have no time to love them.", author: "Mother Teresa" },
  { text: "The greatest discovery of all time is that a person can change his future by merely changing his attitude.", author: "Stephen Covey" },
  { text: "The most important word in the world is hope.", author: "Zig Ziglar" },
  { text: "Non-violence is a weapon of the strong.", author: "Mahatma Gandhi" },
  { text: "You are the only person on earth who can use your ability.", author: "Zig Ziglar" },
  { text: "Your time is limited, don't waste it living someone else's life.", author: "Dale Carnegie" },
  { text: "Proactive people can carry their own weather with them.  Whether it rains or shines makes no difference to them.  They are value driven; and if their value is to produce good quality work, it isn't a function of whether the weather is conducive to it or not.", author: "Stephen Covey" },
  { text: "Always give without remembering and always receive without forgetting", author: "Brian Tracy" },
  { text: "Begin today with the image... of the end of your life as your frame of reference or the criterion by which everything else is examined. By keeping that end clearly in mind, you can make certain that whatever you do on any particular day does not violate the criteria you have defined as supremely important, and that each day in your life contributes in a meaningful way to the vision you have of your life as a whole.", author: "Stephen Covey" },
  { text: "Don't let schooling interfere with your education.", author: "Mark Twain" },
  { text: "Today you are you, that is truer than true. There is no one alive who is you-er than you.", author: "Dr. Seuss" },
  { text: "Comparison is the cardinal sin of modern life. It traps us in a game that we can't win. Once we define ourselves in terms of others, we lose the freedom to shape our own lives.", author: "James C. Collins" },
  { text: "Spread love everywhere you go. Let no one ever come to you without leaving happier.", author: "Mother Teresa" },
  { text: "Even after all this time,the sun never says to the earth, ‘You owe me.’  Look what happens with a love like that.  It lights the whole sky.", author: "Hafiz, 1320-1389" },
  { text: "A person's a person, no matter how small.", author: "Dr. Seuss" },
  { text: "You must believe that you can do it, under any circumstances.", author: "Jack Canfield" },
  { text: "This day I choose to spend in quiet reflection, perfecting my craft, studying my work, and understanding my purpose.", author: "Og Mandino" },
];

// 2. Helper functions outside
const getStatusColor = (status) => {
  switch (status) {
    case 'complete':   return 'bg-emerald-100 text-emerald-800';
    case 'in-process': return 'bg-blue-100 text-blue-800';
    case 'delegated':  return 'bg-amber-100 text-amber-800';
    default:           return 'bg-gray-100 text-gray-800';
  }
};

const getCategoryLabel = (cat) => {
  return cat === 'must-do' ? '🔴 Must Do' : cat === 'should-do' ? '🟡 Should Do' : '🟢 Could Do';
};

const formatDate = (date) => format(date, 'EEEE, MMMM d, yyyy');

// 3. TaskItem outside — blues replaced with CSS vars
const TaskItem = ({
  task,
  taskNumber,
  updateTaskStatus,
  updatingId,
  expandedNotesId,
  setExpandedNotesId,
  movingId,
  setMovingId,
  moveDate,
  setMoveDate,
  moveTask,
  deleteTask,
  deletingId,
  notesText,
  setNotesText,
  saveTaskNotes,
  savingNotesId,
  editingTaskId,
  setEditingTaskId,
  editingTaskText,
  setEditingTaskText,
  saveTaskTitle,
  canSchedule,
  schedulingId,
  setSchedulingId,
  scheduleDate,
  setScheduleDate,
  scheduleStart,
  setScheduleStart,
  scheduleEnd,
  setScheduleEnd,
  scheduleTask,
  savingScheduleId,
  scheduleError,
  setScheduleError,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  isDragOver
}) => {
  const notesInputRef = useRef(null);

  useEffect(() => {
    if (expandedNotesId === task.id) {
      notesInputRef.current?.focus();
    }
  }, [expandedNotesId, task.id]);

  const openSchedulePanel = () => {
    setScheduleDate(task.date || format(new Date(), 'yyyy-MM-dd'));
    setScheduleStart('09:00');
    setScheduleEnd('10:00');
    setScheduleError('');
    setSchedulingId(schedulingId === task.id ? null : task.id);
  };

  return (
    <div
      key={task.id}
      draggable
      onDragStart={() => onDragStart(task.id)}
      onDragOver={(e) => { e.preventDefault(); onDragOver(task.id); }}
      onDrop={() => onDrop(task.id)}
      onDragEnd={onDragEnd}
      className="mb-2 border border-slate-200 rounded-lg transition-shadow hover:shadow-md"
      style={{ position: 'relative', overflow: 'visible' }}
    >
      {/* Insertion indicator — shows exactly where a dragged task will land */}
      {isDragOver && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '3px',
            backgroundColor: 'var(--color-primary)',
            zIndex: 20,
            pointerEvents: 'none',
          }}
        >
          <div style={{
            position: 'absolute', left: '0px', top: '0px',
            width: '9px', height: '9px', borderRadius: '50%',
            backgroundColor: 'var(--color-primary)',
          }} />
        </div>
      )}
   
      <div className="flex items-start gap-3 px-3 py-2 bg-white transition-all">
        {/* Drag handle + number — kept full-size for reliable grabbing even as rows compact */}
        <div
          className="flex flex-col items-center justify-center flex-shrink-0 gap-0.5 cursor-grab active:cursor-grabbing"
          style={{ minHeight: '38px', paddingRight: '2px' }}
        >
          <span className="text-[10px] font-bold text-slate-400 leading-none">{taskNumber}</span>
          <GripVertical size={16} className="text-slate-300" />
        </div>

        {/* Completion checkbox */}
        <button
          onClick={() => updateTaskStatus(task.id, task.status === 'complete' ? 'pending' : 'complete')}
          disabled={updatingId === task.id}
          className={`flex-shrink-0 mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
            updatingId === task.id ? 'opacity-50 cursor-not-allowed' : ''
          }`}
          style={task.status === 'complete'
            ? { backgroundColor: 'var(--color-secondary)', borderColor: 'var(--color-secondary)' }
            : { borderColor: '#cbd5e1' }}
        >
          {updatingId === task.id ? (
            <Loader size={12} className="animate-spin" />
          ) : task.status === 'complete' ? (
            <Check size={14} className="text-white" />
          ) : null}
        </button>

        {/* Title / edit field */}
        <div className="flex-1 min-w-0">
          {editingTaskId === task.id ? (
            <div className="flex gap-2">
              <input
                type="text"
                value={editingTaskText}
                onChange={(e) => setEditingTaskText(e.target.value)}
                className="flex-1 px-2 py-1 text-sm border rounded focus:outline-none"
                style={{ borderColor: 'var(--color-primary)' }}
                autoFocus
              />
              <button
                onClick={() => saveTaskTitle(task.id)}
                className="text-xs px-2 py-1 text-white rounded transition-colors"
                style={{ backgroundColor: 'var(--color-primary)' }}
              >
                Save
              </button>
              <button
                onClick={() => setEditingTaskId(null)}
                className="text-xs px-2 py-1 bg-slate-300 hover:bg-slate-400 text-slate-700 rounded transition-colors"
              >
                Cancel
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-1.5 min-w-0">
              <p
                onClick={() => {
                  setEditingTaskId(task.id);
                  setEditingTaskText(task.title);
                }}
                className={`text-sm font-medium cursor-pointer hover:underline min-w-0 ${
                  task.status === 'complete' ? 'line-through text-slate-400' : 'text-slate-800'
                }`}
              >
                {task.title}
              </p>
              {task.calendarEventLink && (
                <a
                  href={task.calendarEventLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="flex-shrink-0"
                  title="On your Google Calendar — click to open"
                >
                  <CalendarCheck size={14} style={{ color: 'var(--color-secondary)' }} />
                </a>
              )}
            </div>
          )}
        </div>

        {/* Status dropdown */}
        <select
          value={task.status}
          onChange={(e) => updateTaskStatus(task.id, e.target.value)}
          disabled={updatingId === task.id}
          className={`text-xs px-2 py-1 rounded border-0 cursor-pointer flex-shrink-0 ${getStatusColor(task.status)} ${
            updatingId === task.id ? 'opacity-50' : ''
          }`}
        >
          <option value="pending">Pending</option>
          <option value="complete">Complete</option>
          <option value="in-process">In Process</option>
          <option value="delegated">Delegated</option>
        </select>

        {/* Notes toggle */}
        <button
          onClick={() => setExpandedNotesId(expandedNotesId === task.id ? null : task.id)}
          className="p-1 text-slate-400 transition-colors flex-shrink-0 hover:text-slate-600"
          title="Add notes to this task"
        >
          <MessageSquare size={16} />
        </button>

        {/* Schedule on Google Calendar */}
        {canSchedule && (
          <button
            onClick={openSchedulePanel}
            className="p-1 text-slate-400 transition-colors flex-shrink-0"
            style={schedulingId === task.id ? { color: 'var(--color-secondary)' } : {}}
            onMouseEnter={(e) => e.currentTarget.style.color = 'var(--color-secondary)'}
            onMouseLeave={(e) => e.currentTarget.style.color = schedulingId === task.id ? 'var(--color-secondary)' : ''}
            title="Add this task to your Google Calendar"
          >
            <CalendarPlus size={16} />
          </button>
        )}

        {/* Move to date */}
        {movingId === task.id ? (
          <div className="flex gap-1 flex-shrink-0">
            <input
              type="date"
              value={moveDate}
              onChange={(e) => setMoveDate(e.target.value)}
              className="text-xs px-2 py-1 border border-slate-300 rounded"
            />
            <button
              onClick={() => moveTask(task.id)}
              disabled={!moveDate}
              className="text-xs px-2 py-1 text-white rounded transition-colors disabled:opacity-50"
              style={{ backgroundColor: 'var(--color-primary)' }}
            >
              Move
            </button>
            <button
              onClick={() => { setMovingId(null); setMoveDate(''); }}
              className="text-xs px-2 py-1 bg-slate-300 hover:bg-slate-400 text-slate-700 rounded transition-colors"
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            onClick={() => setMovingId(task.id)}
            className="p-1 text-slate-400 hover:text-amber-500 transition-colors flex-shrink-0"
            title="Move this task to a different date"
          >
            <Calendar size={16} />
          </button>
        )}

        {/* Delete */}
        <button
          onClick={() => deleteTask(task.id)}
          disabled={deletingId === task.id}
          className={`p-1 text-slate-400 hover:text-red-500 transition-colors flex-shrink-0 ${
            deletingId === task.id ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          {deletingId === task.id ? (
            <Loader size={16} className="animate-spin text-red-500" />
          ) : (
            <Trash2 size={16} />
          )}
        </button>
      </div>

      {/* Expanded notes panel */}
      {expandedNotesId === task.id && (
        <div className="border-t border-slate-200 p-3" style={{ backgroundColor: 'var(--color-primary-light)' }}>
          <div className="mb-3">
            <label className="block text-xs font-semibold text-slate-600 mb-2">Task Notes</label>
            <textarea
              ref={notesInputRef}
              dir="ltr"
              value={notesText[task.id] || ''}
              onChange={(e) => setNotesText({ ...notesText, [task.id]: e.target.value })}
              placeholder="Add processes, contacts, ideas, or steps for this task..."
              style={{
                width: '100%',
                height: '96px',
                padding: '8px',
                fontSize: '14px',
                border: '1px solid #cbd5e1',
                borderRadius: '8px',
                resize: 'none',
                direction: 'ltr',
                textAlign: 'left'
              }}
            />
          </div>
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => setExpandedNotesId(null)}
              className="px-3 py-1 text-xs font-medium bg-slate-200 hover:bg-slate-300 text-slate-700 rounded transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => saveTaskNotes(task.id)}
              disabled={savingNotesId === task.id}
              className="px-3 py-1 text-xs font-medium text-white rounded transition-colors flex items-center gap-1 disabled:opacity-50"
              style={{ backgroundColor: 'var(--color-primary)' }}
            >
              {savingNotesId === task.id ? <Loader size={12} className="animate-spin" /> : 'Save Notes'}
            </button>
          </div>
        </div>
      )}

      {/* Expanded schedule panel */}
      {schedulingId === task.id && (
        <div className="border-t border-slate-200 p-3" style={{ backgroundColor: 'var(--color-secondary-light)' }}>
          <label className="block text-xs font-semibold text-slate-600 mb-2">
            Add to Google Calendar: "{task.title}"
          </label>
          <div className="flex flex-wrap items-end gap-3 mb-3">
            <div>
              <label className="block text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-1">Date</label>
              <input
                type="date"
                value={scheduleDate}
                onChange={(e) => setScheduleDate(e.target.value)}
                disabled={savingScheduleId === task.id}
                className="text-xs px-2 py-1.5 border border-slate-300 rounded disabled:bg-slate-100"
              />
            </div>
            <div>
              <label className="block text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-1">Start</label>
              <input
                type="time"
                value={scheduleStart}
                onChange={(e) => setScheduleStart(e.target.value)}
                disabled={savingScheduleId === task.id}
                className="text-xs px-2 py-1.5 border border-slate-300 rounded disabled:bg-slate-100"
              />
            </div>
            <div>
              <label className="block text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-1">End</label>
              <input
                type="time"
                value={scheduleEnd}
                onChange={(e) => setScheduleEnd(e.target.value)}
                disabled={savingScheduleId === task.id}
                className="text-xs px-2 py-1.5 border border-slate-300 rounded disabled:bg-slate-100"
              />
            </div>
          </div>

          {scheduleError && (
            <p className="mb-3 text-xs text-red-600 font-medium">{scheduleError}</p>
          )}

          <div className="flex gap-2 justify-end">
            <button
              onClick={() => setSchedulingId(null)}
              disabled={savingScheduleId === task.id}
              className="px-3 py-1 text-xs font-medium bg-slate-200 hover:bg-slate-300 text-slate-700 rounded transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={() => scheduleTask(task)}
              disabled={savingScheduleId === task.id}
              className="px-3 py-1 text-xs font-medium text-white rounded transition-colors flex items-center gap-1 disabled:opacity-50"
              style={{ backgroundColor: 'var(--color-secondary)' }}
            >
              {savingScheduleId === task.id ? <Loader size={12} className="animate-spin" /> : <CalendarPlus size={12} />}
              {savingScheduleId === task.id ? 'Adding...' : 'Add to Calendar'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// 4. TaskCategory outside
const TaskCategory = ({ tasks, categoryLabel, reorderTasks, ...itemProps }) => {
  const [dragOverId, setDragOverId] = useState(null);
  const dragItemId = useRef(null);

  const handleDragStart = (id) => { dragItemId.current = id; };
  const handleDragOver = (id) => { setDragOverId(id); };
  const handleDrop = (targetId) => {
    if (dragItemId.current && dragItemId.current !== targetId) {
      reorderTasks(tasks, dragItemId.current, targetId);
    }
    setDragOverId(null);
  };
  const handleDragEnd = () => {
    dragItemId.current = null;
    setDragOverId(null);
  };

  return (
    <div className="mb-6">
      <h3 className="text-base font-semibold text-slate-700 mb-3 flex items-center gap-2">
        {categoryLabel}
        <span className="text-sm font-medium bg-slate-100 text-slate-600 px-2.5 py-0.5 rounded-full">
          {tasks.length}
        </span>
      </h3>
      <div>
        {tasks.length === 0 ? (
          <p className="text-sm text-slate-400 italic py-3">No tasks in this category</p>
        ) : (
          tasks.map((task, index) => (
            <TaskItem
              key={task.id}
              task={task}
              taskNumber={index + 1}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onDragEnd={handleDragEnd}
              isDragOver={dragOverId === task.id}
              {...itemProps}
            />
          ))
        )}
      </div>
    </div>
  );
};

// ─── Mobile tab switcher ──────────────────────────────────────────────────────
const MobileTab = ({ label, icon: Icon, active, onClick }) => (
  <button
    onClick={onClick}
    style={{
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '6px',
      padding: '10px',
      fontSize: '14px',
      fontWeight: 600,
      border: 'none',
      borderBottom: active ? '2px solid var(--color-primary)' : '2px solid transparent',
      backgroundColor: 'transparent',
      color: active ? 'var(--color-primary)' : '#94a3b8',
      cursor: 'pointer',
      transition: 'color 0.15s, border-color 0.15s',
    }}
  >
    <Icon size={15} />
    {label}
  </button>
);
// ─────────────────────────────────────────────────────────────────────────────

// 5. Main Component
export default function TaskView({ user, onDateChange, gapiReady, googleSignedIn, onGoogleSignIn, onCreateEvent }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [tasks, setTasks] = useState([]);
  const [newTask, setNewTask] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('must-do');
  const [dailyQuote, setDailyQuote] = useState('');
  const [loading, setLoading] = useState(false);
  const [addingTask, setAddingTask] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [movingId, setMovingId] = useState(null);
  const [moveDate, setMoveDate] = useState('');
  const [expandedNotesId, setExpandedNotesId] = useState(null);
  const [notesText, setNotesText] = useState({});
  const [savingNotesId, setSavingNotesId] = useState(null);
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editingTaskText, setEditingTaskText] = useState('');

  // ── Schedule-on-calendar state ────────────────────────────────────────────
  const [schedulingId, setSchedulingId]         = useState(null);
  const [scheduleDate, setScheduleDate]         = useState('');
  const [scheduleStart, setScheduleStart]       = useState('09:00');
  const [scheduleEnd, setScheduleEnd]           = useState('10:00');
  const [savingScheduleId, setSavingScheduleId] = useState(null);
  const [scheduleError, setScheduleError]       = useState('');

  // ── Responsive layout state ───────────────────────────────────────────────
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [mobileTab, setMobileTab] = useState('tasks'); // 'tasks' | 'calendar'

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    const dayOfYear = Math.floor((currentDate - new Date(currentDate.getFullYear(), 0, 0)) / 86400000);
    setDailyQuote(quotes[dayOfYear % quotes.length]);
  }, [currentDate]);

  useEffect(() => {
    loadTasks();
  }, [currentDate, user]);

  const loadTasks = async () => {
    setLoading(true);
    try {
      const dateStr = format(currentDate, 'yyyy-MM-dd');
      const q = query(
        collection(db, 'tasks'),
        where('userId', '==', user.uid),
        where('date', '==', dateStr)
      );
      const snapshot = await getDocs(q);
      const loadedTasks = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      loadedTasks.sort((a, b) => {
        const aOrder = a.sortOrder ?? 9999;
        const bOrder = b.sortOrder ?? 9999;
        return aOrder - bOrder;
      });
      setTasks(loadedTasks);
      const notesMap = {};
      loadedTasks.forEach(task => { notesMap[task.id] = task.notes || ''; });
      setNotesText(notesMap);
    } catch (error) {
      console.error('Error loading tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const addTask = async () => {
    if (!newTask.trim()) return;
    setAddingTask(true);
    try {
      const dateStr = format(currentDate, 'yyyy-MM-dd');
      await addDoc(collection(db, 'tasks'), {
        userId: user.uid,
        title: newTask,
        category: selectedCategory,
        status: 'pending',
        date: dateStr,
        notes: '',
        createdAt: serverTimestamp(),
      });
      setNewTask('');
      loadTasks();
    } catch (error) {
      console.error('Error adding task:', error);
    } finally {
      setAddingTask(false);
    }
  };

  const deleteTask = async (id) => {
    setDeletingId(id);
    try {
      await deleteDoc(doc(db, 'tasks', id));
      loadTasks();
    } catch (error) {
      console.error('Error deleting task:', error);
    } finally {
      setDeletingId(null);
    }
  };

  const updateTaskStatus = async (id, newStatus) => {
    setUpdatingId(id);
    try {
      await updateDoc(doc(db, 'tasks', id), { status: newStatus });
      loadTasks();
    } catch (error) {
      console.error('Error updating task:', error);
    } finally {
      setUpdatingId(null);
    }
  };

  const moveTask = async (id) => {
    if (!moveDate) return;
    setMovingId(id);
    try {
      await updateDoc(doc(db, 'tasks', id), { date: moveDate });
      setMoveDate('');
      loadTasks();
    } catch (error) {
      console.error('Error moving task:', error);
    } finally {
      setMovingId(null);
    }
  };

  const saveTaskNotes = async (id) => {
    setSavingNotesId(id);
    try {
      await updateDoc(doc(db, 'tasks', id), { notes: notesText[id] || '' });
      setExpandedNotesId(null);
    } catch (error) {
      console.error('Error saving notes:', error);
    } finally {
      setSavingNotesId(null);
    }
  };

  const saveTaskTitle = async (id) => {
    if (!editingTaskText.trim()) return;
    try {
      await updateDoc(doc(db, 'tasks', id), { title: editingTaskText });
      setEditingTaskId(null);
      loadTasks();
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  // ── Turn a task into a Google Calendar event ──────────────────────────────
  const scheduleTask = async (task) => {
    if (!scheduleDate || !scheduleStart || !scheduleEnd) {
      setScheduleError('Please fill in the date and times.');
      return;
    }
    setSavingScheduleId(task.id);
    setScheduleError('');
    try {
      // Build local dates directly to avoid the UTC midnight trap
      const [y, m, d]   = scheduleDate.split('-').map(Number);
      const [sh, sm]    = scheduleStart.split(':').map(Number);
      const [eh, em]    = scheduleEnd.split(':').map(Number);
      const start = new Date(y, m - 1, d, sh, sm);
      const end   = new Date(y, m - 1, d, eh, em);

      if (end <= start) {
        setScheduleError('End time must be after start time.');
        return;
      }

      const result = await onCreateEvent({
        title:       task.title,
        description: task.notes || '',
        start,
        end,
      });

      // Stamp the event onto the task so it shows a "scheduled" badge
      await updateDoc(doc(db, 'tasks', task.id), {
        calendarEventId:   result.id,
        calendarEventLink: result.htmlLink || '',
      });

      setSchedulingId(null);
      loadTasks();
    } catch (error) {
      console.error('Error scheduling task on calendar:', error);
      setScheduleError('Could not create the calendar event. Please try again.');
    } finally {
      setSavingScheduleId(null);
    }
  };

  const reorderTasks = async (categoryTasks, draggedId, targetId) => {
    const draggedIndex = categoryTasks.findIndex(t => t.id === draggedId);
    const targetIndex  = categoryTasks.findIndex(t => t.id === targetId);
    if (draggedIndex === -1 || targetIndex === -1) return;

    const reordered = [...categoryTasks];
    const [removed] = reordered.splice(draggedIndex, 1);
    // The blue line means "insert above this row." After removing the dragged
    // item, every index below it shifts up by one — so when dragging downward
    // (dragged was above target), the target's index is now one less. Adjust
    // the insertion point so the item always lands just above the line.
    const insertIndex = draggedIndex < targetIndex ? targetIndex - 1 : targetIndex;
    reordered.splice(insertIndex, 0, removed);

    const reorderedWithSortOrder = reordered.map((task, index) => ({
      ...task,
      sortOrder: index,
    }));

    setTasks(prev => {
      const otherTasks = prev.filter(t => t.category !== removed.category);
      return [...otherTasks, ...reorderedWithSortOrder];
    });

    try {
      await Promise.all(
        reorderedWithSortOrder.map(task =>
          updateDoc(doc(db, 'tasks', task.id), { sortOrder: task.sortOrder })
        )
      );
    } catch (error) {
      console.error('Error saving task order:', error);
      loadTasks();
    }
  };

  const sortWithCompleteAtBottom = (taskList) =>
    [...taskList].sort((a, b) => {
      const aComplete = a.status === 'complete' ? 1 : 0;
      const bComplete = b.status === 'complete' ? 1 : 0;
      if (aComplete !== bComplete) return aComplete - bComplete;
      return (a.sortOrder ?? 9999) - (b.sortOrder ?? 9999);
    });

  const getMustDo   = () => sortWithCompleteAtBottom(tasks.filter(t => t.category === 'must-do'));
  const getShouldDo = () => sortWithCompleteAtBottom(tasks.filter(t => t.category === 'should-do'));
  const getCouldDo  = () => sortWithCompleteAtBottom(tasks.filter(t => t.category === 'could-do'));

  const itemProps = {
    updateTaskStatus,
    updatingId,
    expandedNotesId,
    setExpandedNotesId,
    movingId,
    setMovingId,
    moveDate,
    setMoveDate,
    moveTask,
    deleteTask,
    deletingId,
    notesText,
    setNotesText,
    saveTaskNotes,
    savingNotesId,
    editingTaskId,
    setEditingTaskId,
    editingTaskText,
    setEditingTaskText,
    saveTaskTitle,
    canSchedule: Boolean(onCreateEvent),
    schedulingId,
    setSchedulingId,
    scheduleDate,
    setScheduleDate,
    scheduleStart,
    setScheduleStart,
    scheduleEnd,
    setScheduleEnd,
    scheduleTask,
    savingScheduleId,
    scheduleError,
    setScheduleError,
  };

  // ── Shared header content (date card + quote + add task) ──────────────────
  const headerContent = (
    <>
      {/* Date header card */}
      <div className="mb-6 bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-800">My Day</h1>
            <div className="mt-2">
              <p className="text-xs text-slate-500 uppercase tracking-wide">Today</p>
              <p className="text-sm font-semibold text-slate-700">{formatDate(currentDate)}</p>
            </div>
          </div>

          <div className="flex items-center gap-2 border border-slate-200 rounded-lg p-2 bg-slate-50">
            <button
              onClick={() => { const d = subDays(currentDate, 1); setCurrentDate(d); onDateChange?.(d); }}
              className="p-2 hover:bg-slate-200 rounded-lg transition-colors flex-shrink-0"
              title="Previous day"
            >
              <ChevronLeft size={20} className="text-slate-600" />
            </button>

            <div className="flex flex-col gap-2">
              <input
                type="date"
                value={format(currentDate, 'yyyy-MM-dd')}
                onChange={(e) => { const [y, m, dd] = e.target.value.split('-').map(Number); const d = new Date(y, m - 1, dd); setCurrentDate(d); onDateChange?.(d); }}
                className="px-3 py-1 text-sm border border-slate-300 rounded-lg focus:outline-none cursor-pointer"
              />
              <button
                onClick={() => { const d = new Date(); setCurrentDate(d); onDateChange?.(d); }}
                className="px-3 py-1 text-sm font-medium text-white rounded-lg transition-colors whitespace-nowrap"
                style={{ backgroundColor: 'var(--color-primary)' }}
                title="Jump to today"
              >
                Go to Today
              </button>
            </div>

            <button
              onClick={() => { const d = addDays(currentDate, 1); setCurrentDate(d); onDateChange?.(d); }}
              className="p-2 hover:bg-slate-200 rounded-lg transition-colors flex-shrink-0"
              title="Next day"
            >
              <ChevronRight size={20} className="text-slate-600" />
            </button>
          </div>
        </div>
      </div>

      {/* Daily quote banner */}
      <div
        className="mb-6 p-5 rounded-xl shadow-lg text-white"
        style={{
          background: 'linear-gradient(to bottom right, var(--color-primary), var(--color-secondary))'
        }}
      >
        <p className="text-base font-semibold italic mb-2">"{dailyQuote.text}"</p>
        <p className="text-sm opacity-80">— {dailyQuote.author}</p>
      </div>

      {/* Add task panel */}
      <div className="mb-6 p-4 bg-white rounded-xl border border-slate-200 shadow-sm">
        <label className="block text-xs uppercase font-semibold text-slate-500 mb-3">Add New Task</label>
        <div className="flex gap-2 mb-3">
          <input
            type="text"
            value={newTask}
            onChange={(e) => setNewTask(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && addTask()}
            disabled={addingTask}
            placeholder="What needs to be done?"
            className="flex-1 px-4 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none transition-all disabled:bg-slate-100 disabled:cursor-not-allowed"
          />
          <button
            onClick={addTask}
            disabled={addingTask}
            className="p-2 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            style={{ backgroundColor: 'var(--color-primary)' }}
          >
            {addingTask ? <Loader size={20} className="animate-spin" /> : <Plus size={20} />}
          </button>
        </div>

        {/* Category selector */}
        <div className="flex gap-2 flex-wrap">
          {['must-do', 'should-do', 'could-do'].map(cat => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              disabled={addingTask}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-all disabled:opacity-50 ${
                selectedCategory === cat ? 'ring-2' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
              style={selectedCategory === cat ? {
                backgroundColor: 'var(--color-primary-light)',
                color: 'var(--color-primary)',
                ringColor: 'var(--color-primary)',
                outlineColor: 'var(--color-primary)',
                boxShadow: '0 0 0 2px var(--color-primary)'
              } : {}}
            >
              {getCategoryLabel(cat)}
            </button>
          ))}
        </div>
      </div>
    </>
  );

  // ── Task list content ─────────────────────────────────────────────────────
  const taskListContent = (
    <>
      {loading ? (
        <div className="text-center py-12">
          <Loader size={32} className="mx-auto animate-spin" style={{ color: 'var(--color-primary)' }} />
          <p className="mt-3 text-slate-600">Loading your tasks...</p>
        </div>
      ) : (
        <>
          <TaskCategory tasks={getMustDo()}   categoryLabel="🔴 Must Do"   reorderTasks={reorderTasks} {...itemProps} />
          <TaskCategory tasks={getShouldDo()} categoryLabel="🟡 Should Do" reorderTasks={reorderTasks} {...itemProps} />
          <TaskCategory tasks={getCouldDo()}  categoryLabel="🟢 Could Do"  reorderTasks={reorderTasks} {...itemProps} />

          {tasks.length === 0 && (
            <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
              <Check size={48} className="mx-auto mb-3 opacity-60" style={{ color: 'var(--color-secondary)' }} />
              <p className="text-slate-500 text-lg font-medium">No tasks for today!</p>
              <p className="text-slate-400 text-sm mt-1">Add one to get started.</p>
            </div>
          )}
        </>
      )}
    </>
  );

  // ─── Mobile layout ────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <div>
        {headerContent}

        {/* Tab switcher */}
        <div style={{
          display: 'flex',
          backgroundColor: '#fff',
          border: '1px solid #e2e8f0',
          borderRadius: '12px 12px 0 0',
          overflow: 'hidden',
          marginBottom: 0,
        }}>
          <MobileTab
            label="Tasks"
            icon={Check}
            active={mobileTab === 'tasks'}
            onClick={() => setMobileTab('tasks')}
          />
          <MobileTab
            label="Calendar"
            icon={Calendar}
            active={mobileTab === 'calendar'}
            onClick={() => setMobileTab('calendar')}
          />
        </div>

        {/* Tab content */}
        <div style={{
          backgroundColor: '#fff',
          border: '1px solid #e2e8f0',
          borderTop: 'none',
          borderRadius: '0 0 12px 12px',
          padding: mobileTab === 'tasks' ? '16px' : '0',
          minHeight: '400px',
        }}>
          {mobileTab === 'tasks' ? (
            taskListContent
          ) : (
            <div style={{ height: '520px' }}>
              <GoogleCalendarPanel
                currentDate={currentDate}
                gapiReady={gapiReady}
                googleSignedIn={googleSignedIn}
                onGoogleSignIn={onGoogleSignIn}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  // ─── Desktop layout (60/40 split) ─────────────────────────────────────────
  return (
    <div>
      {headerContent}

      <div style={{
        display: 'flex',
        gap: '20px',
        alignItems: 'flex-start',
      }}>
        {/* Tasks — 60% */}
        <div style={{ flex: '0 0 60%', minWidth: 0 }}>
          {taskListContent}
        </div>

        {/* Calendar panel — 40%, sticky so it stays in view while scrolling tasks */}
        <div style={{
          flex: '0 0 calc(40% - 20px)',
          minWidth: 0,
          position: 'sticky',
          top: '20px',
          height: 'calc(100vh - 160px)',
          maxHeight: '800px',
        }}>
          <GoogleCalendarPanel
            currentDate={currentDate}
            gapiReady={gapiReady}
            googleSignedIn={googleSignedIn}
            onGoogleSignIn={onGoogleSignIn}
          />
        </div>
      </div>
    </div>
  );
}
